'use client';

import { useMemo, useState } from 'react';

import { ManageSubscriptionModal } from '@ps/features/subscription/configuration/components/flows/ManageSubscriptionModal';
import {
  isAvailableSubscription,
  subscriptionsToList,
} from '@ps/features/subscription/configuration/functions/access-control';
import { getSubscriptionDisplayName } from '@ps/features/subscription/configuration/functions/subscription-display';
import { useFetchAllSubscriptions } from '@ps/features/subscription/configuration/hooks/useFetchAllSubscriptions';
import type {
  ActiveSubscription,
  PopStoreExtendedSubscription,
} from '@ps/models/lib/creator-subscription';
import { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import type {
  ConfigureSubscriptions,
  SubscriptionLevelOption,
} from '@ps/ui/molecules/SubscriptionLevelPicker';
import { SubscriptionLevelPicker } from '@ps/ui/molecules/SubscriptionLevelPicker';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { ChannelVisibility } from '../../types/ChannelVisibility';

interface Props {
  value: PopStoreExtendedSubscriptionLevel;
  onChange: (next: PopStoreExtendedSubscriptionLevel) => void;
  visibility: ChannelVisibility;
  disabled?: boolean;
}

const kBaseOptions: SubscriptionLevelOption<PopStoreExtendedSubscriptionLevel>[] =
  [
    {
      value: PopStoreExtendedSubscriptionLevel.FOLLOWER,
      title: 'Follower (Free)',
    },
    {
      value: PopStoreExtendedSubscriptionLevel.SUBSCRIBER,
      title: 'Subscriber',
      subTitle: 'and higher',
    },
    {
      value: PopStoreExtendedSubscriptionLevel.FAN,
      title: 'Fan',
      subTitle: 'and higher',
    },
    {
      value: PopStoreExtendedSubscriptionLevel.VIP,
      title: 'VIP',
      subTitle: 'and higher',
    },
  ];

/**
 * Subscription-level field for the Create / Edit Channel modals.
 * Loads the creator's configured tiers via `useFetchAllSubscriptions`, builds
 * the picker options with custom tier names + `(not configured)` annotations,
 * and branches help text by visibility.
 *
 * Selecting an unconfigured tier runs the shared picker's "Subscription Not
 * Configured — configure now?" confirm chain and opens
 * {@link ManageSubscriptionModal} — the SAME flow the post editor's
 * `SubscribeToViewLevelPicker` uses. On close, the picker refreshes the tiers
 * and commits the selection only if it became configured.
 */
export function ChannelSubscriptionLevelField({
  value,
  onChange,
  visibility,
  disabled,
}: Props) {
  const { creatorId } = useCommunityContext();
  const { subscriptions, loading, refresh } = useFetchAllSubscriptions(
    creatorId ?? undefined,
  );

  const activeSubscriptions = useMemo<ActiveSubscription[] | undefined>(() => {
    if (subscriptions == null) return undefined;
    return subscriptionsToList(subscriptions).filter(isAvailableSubscription);
  }, [subscriptions]);

  const options = useMemo(() => {
    return kBaseOptions.map(option => {
      if (option.value === PopStoreExtendedSubscriptionLevel.FOLLOWER) {
        return option;
      }
      const subscription = activeSubscriptions?.find(
        sub => (sub.platform_sub_level as string) === (option.value as string),
      );
      const title = subscription
        ? getSubscriptionDisplayName(subscription, option.value)
        : option.title;
      const subTitle =
        activeSubscriptions != null && subscription == null
          ? '(not configured)'
          : option.subTitle;
      return { ...option, title, subTitle };
    });
  }, [activeSubscriptions]);

  const [configure, setConfigure] = useState<{
    level: PopStoreExtendedSubscription['level'];
    onDone: () => void;
  } | null>(null);

  const onConfigureSubscriptions: ConfigureSubscriptions = ({
    defaultSubscriptionLevel,
    onDone,
  }) =>
    // The picker types the level as `PopStoreExtendedSubscriptionLevel`, the
    // modal as `PopStoreExtendedSubscription['level']` (paid tiers use the
    // parallel `PopStoreSubscriptionLevel` enum). Same string values, distinct
    // nominal enums — bridge them the way the rest of this feature does.
    setConfigure({
      level:
        defaultSubscriptionLevel as unknown as PopStoreExtendedSubscription['level'],
      onDone,
    });

  const helpText =
    visibility === ChannelVisibility.Public
      ? 'Below subscription will be required to post messages.'
      : 'Below subscription will be required to join this channel and participate in discussions thereafter.';

  return (
    <fieldset>
      <legend className="text-content-1 text-sm font-medium">
        Subscription Level<span className="text-red-400">*</span>
      </legend>
      <p className="text-content-3 mb-3 mt-1 text-xs">{helpText}</p>
      <SubscriptionLevelPicker<PopStoreExtendedSubscriptionLevel>
        options={options}
        value={value}
        onChange={onChange}
        activeSubscriptions={activeSubscriptions}
        refreshSubscriptions={refresh}
        onConfigureSubscriptions={onConfigureSubscriptions}
        disabled={disabled || loading}
        // Drop the 64px floor + tighten padding — QA flagged these tiles as too
        // tall inside the channel form.
        itemClassName="min-h-0 py-2"
      />
      <ManageSubscriptionModal
        open={configure != null}
        defaultSubscriptionLevel={configure?.level}
        onOpenChange={next => {
          if (!next) {
            const onDone = configure?.onDone;
            setConfigure(null);
            onDone?.();
          }
        }}
      />
    </fieldset>
  );
}
